import React, { useEffect, useCallback, useState, useMemo } from "react";
import MappingTopBox from "./CreatorTools/MappingTool/components/MappingTopBox";
import { doc, setDoc, collection, query, where, getDocs, CollectionReference } from "firebase/firestore";
import { useFirestore, useFirestoreCollectionData } from "reactfire";
import { toast } from "react-toastify";
import { appFeatures } from "../shared/common/AppFeatures";
import { IDatabaseLineItem } from "../shared/common/EstimateEngine/EstimateEngineTypes";
import { EstimateType } from "@rebuild/shared";
import LoadingAnimation from "../components/ui/utils/LoadingAnimation";
import { RulesTable } from "./components/RulesTable";
import { DraftRulesTable } from "./components/DraftRulesTable";
import SharedUtils from "../shared/common/SharedUtils";
import {
  IncludeOrExclude,
  Rule,
  RuleInputLineItem,
  RuleRunCriteria,
  RuleRunCriteriaField,
  RULE_TEMPLATES,
  RuleTemplateInputField,
} from "@rebuild/shared";
import { useOrganizations } from "../hooks/rebuildBackendFetchHooks";
import { useLineItemTemplates } from "../hooks/useLineItemTemplates";
import { InputFieldComponent } from "./components/InputFields/InputFieldComponent";
import { validateRuleInputs } from "../utils/validateRuleInputs";
import { RulesEngineValidationStatus } from "./RulesEngineValidationStatus";
import { CreateRulePopup } from "./RuleEnginePopup";
import { createRule, fetchRules, updateRule, SaveRuleBody } from "./rulesEngineAPI";

const RulesEnginePage: React.FC = () => {
  const firestore = useFirestore();

  const ruleTemplates = RULE_TEMPLATES;
  const [existingRules, setExistingRules] = useState<Rule[]>([]);
  const [isLoadingRules, setIsLoadingRules] = useState<boolean>(true);
  const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);
  const [selectedRule, setSelectedRule] = useState<Rule>();

  const refreshData = useCallback(() => {
    const getExistingRules = async () => {
      setIsLoadingRules(true);
      try {
        const rules = await fetchRules();
        setExistingRules(rules);
      } finally {
        setIsLoadingRules(false);
      }
    };

    getExistingRules();
  }, [firestore]);

  useEffect(() => refreshData(), []);

  const onClose = () => {
    setSelectedRule(undefined);
    setIsPopupOpen(false);
  };

  const onRuleSave = (rule: Rule, note: string) => {
    const saveRule = async (rule: Rule) => {
      try {
        // Strip the server-owned uuid and audit trail; send the note so the server can attach it to the event.
        const { uuid, auditTrail, ...rest } = rule;
        const body = { ...rest, note } as SaveRuleBody;
        if (selectedRule) {
          await updateRule(selectedRule.uuid, body);
          toast.success(`Successfully updated rule!`);
        } else {
          await createRule(body);
          toast.success(`Successfully created new rule!`);
        }
        refreshData();
        onClose();
      } catch (e: any) {
        toast.error(`Failed to save rule: ${e.message}`);
      }
    };

    saveRule(rule);
  };

  const onRuleSelect = (rule: Rule) => {
    setSelectedRule(rule);
    setIsPopupOpen(true);
  };

  const draftRules = useMemo(() => existingRules.filter((rule) => rule.isDraft), [existingRules]);
  const nonDraftRules = useMemo(() => existingRules.filter((rule) => !rule.isDraft), [existingRules]);

  return (
    <div className="min-h-screen bg-gray-50 w-[100%]">
      <MappingTopBox />
      <div className="p-6">
        {!isLoadingRules && !!nonDraftRules?.length && (
          <RulesEngineValidationStatus rules={nonDraftRules} onRuleSelect={onRuleSelect} />
        )}

        {!isLoadingRules && !!draftRules?.length && (
          <DraftRulesTable draftRules={draftRules} onRuleSelect={onRuleSelect} />
        )}

        <div className="mb-6 flex justify-between items-center">
          <button
            onClick={() => setIsPopupOpen(true)}
            disabled={!ruleTemplates?.length}
            className="bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-lg transition-colors"
          >
            Create Rule
          </button>
        </div>

        {isLoadingRules ? (
          <LoadingAnimation fullScreen={false} />
        ) : (
          !!existingRules?.length &&
          ruleTemplates && (
            <RulesTable rules={nonDraftRules} ruleTemplates={ruleTemplates} onRuleSelect={onRuleSelect} />
          )
        )}
      </div>

      {isPopupOpen && ruleTemplates && (
        <CreateRulePopup ruleTemplates={ruleTemplates} onClose={onClose} onRuleSave={onRuleSave} rule={selectedRule} />
      )}
    </div>
  );
};

export default RulesEnginePage;
